import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce } from 'lodash';
import { useGetProfitAndLossCalculations } from 'services/calculations/profitAndLoss/hooks';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import {
  convertToOpposite,
  PERIODS_OPERATION_TYPES,
  summarizeSubtractPeriodValues,
} from 'components/BusinessToolForm/utils/helpers';

import ToastService from 'helpers/ToastService';

import { IFormContentTypes, observedFields } from '../../utils/form';

export const useProfitAndLossCalculation = () => {
  const { id: businessPlanId = '' } = useParams();

  const { setValue } = useFormContext();

  const getProfitAndLossCalculation = useGetProfitAndLossCalculations();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [
    grossFieldName,
    incomeTaxFieldName,
    netIncomeFieldName,
    totalOperatingExpenseFieldName,
    incomeTaxField,
    netProfitField,
    netProfitForIndirectField,
    incomeTaxIndirectField,
  ] = observedFields.profitAndLoss.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (profitAndLossData, unit) => {
        const params = {
          unit,
          businessPlanId,
          profitAndLossData,
        };

        try {
          const { grossProfit, incomeTax, profit, totalOperatingExpense } =
            await getProfitAndLossCalculation.mutateAsync(params);
          const negativeIncomeTax = convertToOpposite(incomeTax);
          setValue(grossFieldName, grossProfit);
          setValue(incomeTaxFieldName, incomeTax);
          setValue(incomeTaxField, negativeIncomeTax);
          setValue(netIncomeFieldName, profit);
          setValue(totalOperatingExpenseFieldName, totalOperatingExpense);
          setValue(netProfitField, profit);
          setValue(netProfitForIndirectField, profit);
          setValue(incomeTaxIndirectField, negativeIncomeTax);
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, 500),

    [],
  );

  const profitAndLossCalculationHandler = useCallback(
    async (content: IFormContentTypes['profitAndLoss']) => {
      if (!canAccess) return;

      const [operationUnit, taxRate, financialIncomeDataArray, nonOperatingIncomeDataArray] = content;
      const normalizedTaxRate = taxRate || '0';
      const financialIncomeData = financialIncomeDataArray[0];
      const nonOperatingIncomeData = summarizeSubtractPeriodValues(
        [...nonOperatingIncomeDataArray.slice(2, 5)],
        PERIODS_OPERATION_TYPES.ADD,
      );
      const nonOperatingExpense = summarizeSubtractPeriodValues(
        [...nonOperatingIncomeDataArray.slice(2, 4)],
        PERIODS_OPERATION_TYPES.ADD,
      );
      const profitAndLossData = {
        taxRate: normalizedTaxRate,
        financialIncomeData,
        nonOperatingIncomeData,
        nonOperatingExpense,
      };
      const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;

      debouncedUpdateFunction(profitAndLossData, unit);
    },

    [debouncedUpdateFunction],
  );

  return profitAndLossCalculationHandler;
};
